/**
 * GlobalScannerTest - Test page for global barcode scanner hook
 *
 * This page is dedicated to testing and debugging the useGlobalBarcodeScanner hook.
 * It provides a simple interface to verify that global scanning works correctly
 * without any complex business logic.
 *
 * @author BizClick WMS
 */

import React, { useState } from 'react';
import { Card } from '../Card';
import { Scan, CheckCircle, Trash2, Copy, CheckCheck } from 'lucide-react';
import { useSimpleBarcodeScanner } from '../../../hooks/useSimpleBarcodeScanner';

interface ScanResult {
  id: string;
  barcode: string;
  timestamp: Date;
  length: number;
}

export const GlobalScannerTest: React.FC = () => {
  const [scanResults, setScanResults] = useState<ScanResult[]>([]);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Use the simple barcode scanner hook (proven working logic)
  const scanner = useSimpleBarcodeScanner({
    onScan: (barcode) => {
      // Simple callback - just log the scanned barcode
      console.log('Scanned:', barcode);

      const result: ScanResult = {
        id: Date.now().toString(),
        barcode: barcode,
        timestamp: new Date(),
        length: barcode.length,
      };

      setScanResults(prev => [result, ...prev]);
    },
    enabled: true,
  });

  const handleClearResults = () => {
    setScanResults([]);
  };

  const handleCopyBarcode = (barcode: string, id: string) => {
    navigator.clipboard.writeText(barcode);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="space-y-6 p-6">
      <Card className="p-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <Scan className="text-blue-600" size={32} />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Global Scanner Test</h2>
              <p className="text-sm text-gray-600">Test useGlobalBarcodeScanner hook</p>
            </div>
          </div>

          {/* Scanner Toggle */}
          <button
            onClick={() => scanner.setEnabled(!scanner.isEnabled)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all whitespace-nowrap ${
              scanner.isEnabled
                ? 'bg-green-100 text-green-800 hover:bg-green-200 border-2 border-green-300'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border-2 border-gray-300'
            }`}
          >
            <CheckCircle className={scanner.isEnabled ? 'text-green-600' : 'text-gray-500'} size={18} />
            <span className="font-medium text-sm">
              Scanner: {scanner.isEnabled ? 'PÅ' : 'AV'}
            </span>
          </button>
        </div>

        {/* Scanner Status */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className={`p-4 rounded-lg ${
            scanner.isEnabled ? 'bg-green-50 border-2 border-green-200' : 'bg-gray-50 border-2 border-gray-200'
          }`}>
            <p className="text-xs text-gray-600 mb-1">Status</p>
            <p className={`text-lg font-semibold ${
              scanner.isEnabled ? 'text-green-800' : 'text-gray-600'
            }`}>
              {scanner.isEnabled ? 'Aktiv' : 'Inaktiv'}
            </p>
          </div>

          <div className={`p-4 rounded-lg ${
            scanner.isScanning ? 'bg-yellow-50 border-2 border-yellow-300 animate-pulse' : 'bg-gray-50 border-2 border-gray-200'
          }`}>
            <p className="text-xs text-gray-600 mb-1">Skanning</p>
            <p className={`text-lg font-semibold ${
              scanner.isScanning ? 'text-yellow-800' : 'text-gray-600'
            }`}>
              {scanner.isScanning ? 'Pågår...' : 'Venter'}
            </p>
          </div>

          <div className="p-4 rounded-lg bg-blue-50 border-2 border-blue-200">
            <p className="text-xs text-gray-600 mb-1">Totalt skannet</p>
            <p className="text-lg font-semibold text-blue-800">
              {scanResults.length}
            </p>
          </div>
        </div>

        {/* Scanning Progress */}
        {scanner.isScanning && scanner.scanProgress && (
          <div className="p-4 rounded-lg bg-yellow-50 border-2 border-yellow-300 mb-4">
            <p className="text-sm font-semibold text-yellow-800 mb-2">
              Akkumulerer tegn... ({scanner.scanProgress.length} tegn)
            </p>
            <p className="font-mono text-sm text-yellow-900 bg-white px-3 py-2 rounded border border-yellow-300 break-all">
              {scanner.scanProgress}
            </p>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Instruksjoner:</strong> Sørg for at "Scanner: PÅ" er aktivert, og skann en strekkode hvor som helst på siden (ikke klikk i noe tekstfelt først).
            Skanneren vil automatisk fange opp input fra skanneren og vise resultatet nedenfor.
          </p>
        </div>
      </Card>

      {/* Scan Results */}
      {scanResults.length > 0 && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Skannede strekkoder ({scanResults.length})
            </h3>
            <button
              onClick={handleClearResults}
              className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors flex items-center gap-2"
            >
              <Trash2 size={16} />
              Tøm alle
            </button>
          </div>

          <div className="space-y-3">
            {scanResults.map((result) => (
              <div
                key={result.id}
                className="p-4 bg-gray-50 border-2 border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs text-gray-500">
                        {result.timestamp.toLocaleTimeString('no-NO')}
                      </span>
                      <span className="text-xs text-gray-400">•</span>
                      <span className="text-xs text-gray-500">
                        {result.length} tegn
                      </span>
                    </div>
                    <p className="font-mono text-lg text-gray-900 break-all">
                      {result.barcode}
                    </p>
                  </div>
                  <button
                    onClick={() => handleCopyBarcode(result.barcode, result.id)}
                    className="ml-4 p-2 hover:bg-gray-200 rounded transition-colors"
                    title="Kopier strekkode"
                  >
                    {copiedId === result.id ? (
                      <CheckCheck className="text-green-600" size={20} />
                    ) : (
                      <Copy className="text-gray-600" size={20} />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Debug Info */}
      <Card className="p-6 bg-gray-50">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Debug Info</h3>
        <div className="space-y-2 text-sm font-mono">
          <div className="flex justify-between">
            <span className="text-gray-600">scanner.isEnabled:</span>
            <span className="text-gray-900">{scanner.isEnabled.toString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">scanner.isScanning:</span>
            <span className="text-gray-900">{scanner.isScanning.toString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">scanner.scanProgress:</span>
            <span className="text-gray-900">"{scanner.scanProgress}"</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">scanResults.length:</span>
            <span className="text-gray-900">{scanResults.length}</span>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default GlobalScannerTest;
